import React, { useState } from 'react';
import { Alert, TextField, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getAuthHeaders, makeApiCall } from '../../utils/apiConfig';
import { FormDialog } from '../../components/ui';
import { useToast } from '../../contexts/ToastContext';

async function postStatus(orgId: string, verb: 'suspend' | 'reactivate', reason?: string) {
  const headers = await getAuthHeaders();
  const res = await makeApiCall(`/admin/organizations/${orgId}/${verb}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(reason ? { reason } : {}),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `Failed to ${verb} the store`);
  }
  return res.json();
}

/** Suspend (with a reason) and reactivate for one store; refreshes every Stores query on success. */
export function useStoreStatusMutations() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-store-accounts'] });
    queryClient.invalidateQueries({ queryKey: ['admin-store-details'] });
  };
  const suspend = useMutation({
    mutationFn: ({ orgId, reason, name }: { orgId: string; reason: string; name: string }) =>
      postStatus(orgId, 'suspend', reason).then(() => name),
    onSuccess: (name) => {
      refresh();
      showToast(`${name} suspended`, 'success');
    },
  });
  const reactivate = useMutation({
    mutationFn: ({ orgId, name }: { orgId: string; name: string }) =>
      postStatus(orgId, 'reactivate').then(() => name),
    onSuccess: (name) => {
      refresh();
      showToast(`${name} reactivated`, 'success');
    },
    onError: (e: Error) => showToast(e.message, 'error'),
  });
  return { suspend, reactivate };
}

interface SuspendStoreDialogProps {
  store: { id: string; name: string } | null;
  onClose: () => void;
}

export const SuspendStoreDialog: React.FC<SuspendStoreDialogProps> = ({ store, onClose }) => {
  const { suspend } = useStoreStatusMutations();
  const [reason, setReason] = useState('');

  const close = () => {
    setReason('');
    suspend.reset();
    onClose();
  };

  return (
    <FormDialog
      open={!!store}
      title={`Suspend ${store?.name ?? ''}?`}
      onClose={close}
      onSubmit={() =>
        store &&
        suspend.mutate({ orgId: store.id, reason: reason.trim(), name: store.name }, { onSuccess: close })
      }
      submitLabel="Suspend store"
      submitColor="error"
      submitDisabled={!reason.trim()}
      loading={suspend.isPending}
      maxWidth="xs"
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Marks the store suspended and hides it from brand store lists. Listings, users and data stay as they are, and
        you can reactivate any time. Suspending does not yet sign members out or stop scheduled syncs.
      </Typography>
      <TextField
        fullWidth
        multiline
        minRows={2}
        size="small"
        label="Reason"
        helperText="Required. Shown in the audit log."
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        inputProps={{ maxLength: 500 }}
      />
      {suspend.isError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {(suspend.error as Error).message}
        </Alert>
      )}
    </FormDialog>
  );
};
